import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./capability-03.css";
import decisionInsightVideo from "../../assets/capability/Order & Inventory Tracker.mp4";
import { useLanguage } from "../../i18n/LanguageContext";

gsap.registerPlugin(ScrollTrigger);

export default function Capability03() {
  const rootRef = useRef(null);
  const { tr } = useLanguage();

  useEffect(() => {
    const ctx = gsap.context(() => {
      const root = rootRef.current;
      if (!root) return;

      const isMobile = window.innerWidth <= 768;

      // Refresh ScrollTrigger saat resize
      const handleResize = () => {
        ScrollTrigger.refresh();
      };
      window.addEventListener("resize", handleResize);

      // === FADE SCROLL HINT (INTRO) ===
      // Adapted from provided logic
      gsap.to(".cap03-scroll", {
        autoAlpha: 0,
        duration: 0.2,
        scrollTrigger: {
          trigger: document.body,
          start: "top top",
          end: "top top-=1",
          toggleActions: "play none reverse none",
        },
      });

      // === LETTER SCROLL ANIMATION ===
      // Adapted from provided logic
      const hcList = root.querySelector(".cap03-list");
      if (hcList && !isMobile) {
        gsap.to(".cap03-letter", {
          yPercent: 100,
          ease: "power1.inOut",
          scrollTrigger: {
            trigger: hcList,
            start: "33.33% bottom",
            end: "100% 80%",
            scrub: 1,
          },
          stagger: {
            each: 0.05,
            from: "random",
          },
        });
      }

      // === SLIDE PPT STYLE (like capability.jsx) ===
      // Set initial state
      gsap.set(root, { autoAlpha: 1, y: 0 });

      if (hcList) {
        gsap.set(hcList, { opacity: 1, y: 0 });
      }

      // Mobile: Skip pinning and complex fade animations
      if (isMobile) {
        // Mobile: no title animation

        const slideHeight = window.innerHeight;

        // Pin section for full viewport height (like PowerPoint)
        ScrollTrigger.create({
          trigger: root,
          start: "top top",
          end: `+=${slideHeight}`,
          pin: root,
          pinSpacing: true,
          scrub: false,
        });
      } else {
        // Desktop: Full pinning and fade animations
      const slideHeight = window.innerHeight;

      // Pin section for full viewport height (like PowerPoint)
      ScrollTrigger.create({
        trigger: root,
        start: "top top",
        end: `+=${slideHeight}`,
        pin: root,
        pinSpacing: true,
        scrub: false,
      });

      // Fade in animation when section enters viewport (support scroll up)
        // Only fade content, not background
        if (hcList) {
      gsap.fromTo(
            hcList,
            { opacity: 0, y: 30 },
        {
              opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: root,
            start: "top 80%",
            end: "top 20%",
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
        }

      // Fade out animation when section exits (support scroll up)
        // Only fade content, keep background visible to prevent black screen
        if (hcList) {
      gsap.fromTo(
            hcList,
            { opacity: 1, y: 0 },
        {
              opacity: 0,
          y: -30,
          duration: 1,
          ease: "power2.in",
          scrollTrigger: {
            trigger: root,
            start: "bottom top",
            end: `bottom+=${slideHeight * 0.5} top`,
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
        }
      }

      // Refresh setelah setup
      ScrollTrigger.refresh();

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div style={{ backgroundColor: "#D8D8D8" }}>
      <section id="solutions-03" className="cap03-section" ref={rootRef}>
        

        <div className="cap03-header">
          <ul className="cap03-list cap03-list-horizontal">
          <li>
              {tr("Order", "Order").split("").map((char, i) => (
                <span key={i} className="cap03-letter">
                <span>{char}</span>
                <span>{char}</span>
              </span>
            ))}
          </li>

          <li>
              {"&".split("").map((char, i) => (
                  <span key={i} className="cap03-letter">
                  <span>{char}</span>
                  <span>{char}</span>
                </span>
              ))}
          </li>

          <li>
              {tr("Inventory", "Inventori").split("").map((char, i) => (
                <span key={i} className="cap03-letter">
                <span>{char}</span>
                <span>{char}</span>
              </span>
            ))}
          </li>

          <li>
              {tr("Tracker", "Tracker").split("").map((char, i) => (
                <span key={i} className="cap03-letter">
                <span>{char}</span>
                <span>{char}</span>
              </span>
            ))}
          </li>
        </ul>
        </div>
        
        <p className="cap03-paragraph">
          {tr(
            "AI that helps you monitor stock, track orders, and flag delays - even across multiple marketplaces",
            "AI yang membantu memantau stok, melacak order, dan menandai keterlambatan — bahkan di banyak marketplace"
          )}
        </p>
        <p className="cap03-paragraph cap03-note">
          {tr(
            "Designed for online business owners selling physical products or bundles",
            "Dirancang untuk online business owner yang menjual produk fisik atau bundle"
          )}
        </p>
        <p className="cap03-num cap03-num-bottom">(03)</p>
        <video
          className="cap03-video"
          src={decisionInsightVideo}
          autoPlay
          loop
          muted
          playsInline
        />
      </section>
    </div>

  );
}
